import {ChangeDetectionStrategy, Component, inject, input, OnInit, signal, WritableSignal} from '@angular/core';
import {catchError, EMPTY} from 'rxjs';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {EntityLoanViewModel} from '../../entity-loans/view-models/entity-loan.view-model';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {EntityLoanTransactionViewModel} from '../../entity-loans/view-models/entity-loan-transaction.view-model';
import {Keys} from '@shared/keys';
import {EntityLoanService} from '../../entity-loans/services/entity-loan.service';
import {EntityLoanTransactionConverterService} from '../../entity-loans/services/entity-loan-transaction-converter.service';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {EntityTransactionModalComponent} from '../../shared/entity-transaction-modal/entity-transaction-modal.component';

@Component({
    selector: 'app-entity-loan-details-modal',
    templateUrl: './entity-loan-details-modal.component.html',
    styleUrls: ['./entity-loan-details-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ClientsideDataService],
    imports: [EntityTransactionModalComponent]
})
export class EntityLoanDetailsModalComponent implements OnInit {

  readonly modalDataInput = input.required<EntityLoanViewModel>();

  readonly entityLoan: WritableSignal<EntityLoanViewModel> = signal(undefined as unknown as EntityLoanViewModel);

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);

  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('date');
  readonly sortOrder = signal(-1);
  readonly currentPage = signal<PageViewModel<EntityLoanTransactionViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });
  private searchString = '';
  private pageNumber = 1;
  private readonly pageSize = 20;
  private readonly SEARCH_FIELDS: Keys<EntityLoanTransactionViewModel> = ['description'];

  private readonly entityLoanService = inject(EntityLoanService);
  private readonly entityLoanTransactionConverterService = inject(EntityLoanTransactionConverterService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly clientSideDataService = inject<ClientsideDataService<EntityLoanTransactionViewModel>>(ClientsideDataService);

  ngOnInit() {
    this.entityLoan.set(this.modalDataInput());

    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Loans);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    const loan = this.entityLoan();
    if (!loan.portfolioNumber || !loan.id) {
      // If the Portfolio Number or Asset Code is undefined or blank, show the modal with no data in the table (no call to BFF)
      this.dataLoading.set(false);
      this.dataEmpty.set(true);
      this.dataAvailable.set(false);
      return;
    }

    this.entityLoanService.getEntityLoanTransactions(loan.entityCode, loan.id).pipe(
      catchError(error => {
        console.error('EntityLoanDetailsModal: failed to load transactions', error);
        this.dataLoading.set(false);
        this.dataEmpty.set(true);
        this.dataAvailable.set(false);
        return EMPTY;
      })
    ).subscribe(transactionList => {
      const transactionVMs = this.entityLoanTransactionConverterService.convert(transactionList);
      this.clientSideDataService.initialize(transactionVMs);
      this.updateData();
      this.dataLoading.set(false);
      this.dataEmpty.set(transactionVMs.length === 0);
      this.dataAvailable.set(!this.dataEmpty());
    });
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber = 1;
    this.updateData();
  }

  search(searchTerm: string): void {
    this.searchString = searchTerm;
    this.pageNumber = 1;
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber = targetPage;
    this.updateData();
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Posting Date',
        dataProperty: 'date',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '25%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Description',
        dataProperty: 'description',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '50%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount',
        dataProperty: 'amount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '20%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.update(v => v * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.LoanTransactions, this.sortField(), this.sortOrder());
  }

  private updateData(): void {
    this.currentPage.set(this.clientSideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString,
      this.pageNumber,
      this.pageSize
    ));
  }
}
